/**
 * Правка количества уже добавленной допродажи в МСК Газобетоне.
 *
 * «Дополнить машину» занимает уже оплаченное свободное место, поэтому увеличение
 * количества не должно молча выходить за него и заводить лишний рейс. Уменьшение
 * разрешено всегда.
 *
 * Здесь нет ни расчёта доставки, ни норм, ни выбора машины: на вход приходят готовые
 * паллетоместа позиции и готовое свободное место из модели загрузки.
 */
import { getUpsellPalletPlacesForQuantity } from './buildGazCommercialProposalBlocks.js';
import { isUblokItem } from './gazUpsellMetrics.js';
import { palletsWord } from '../../utils/gazDeclensions.js';

export const UPSELL_NO_NORM_MESSAGE = 'Нельзя проверить вместимость: у позиции нет нормы поддона.';

/**
 * Позиции, которые завод отгружает только целыми поддонами.
 *
 * U-блоки поштучно не продаются. Клей и противоморозная добавка продаются мешками
 * и вёдрами, поэтому под правило не попадают.
 *
 * Признак берётся готовым из gazUpsellMetrics — тем же, по которому окно дозагрузки
 * уже подставляет полный поддон и помечает позицию «Под заказ». Своей копии правила
 * здесь нет: разойтись им негде.
 */
export const isPalletOnlyUpsell = isUblokItem;

/** Штук на поддоне у позиции заказа: норма приходит готовой из номенклатуры. */
const unitsPerPalletOf = (material) => {
  const raw = Number(
    material?.unitsPerPallet
    ?? material?.units_per_pallet
    ?? material?.UNITS_PER_PALLET
    ?? material?.number_per_pallet
    ?? material?.NUMBER_PER_PALLET
  );
  return Number.isFinite(raw) && raw > 0 ? raw : null;
};

/**
 * Количество, дотянутое до целого поддона.
 *
 * Только вверх: округление вниз недодало бы клиенту то, что он попросил. Ровно то
 * же правило работает у газобетона в разделе «Товары», поэтому поведение знакомое.
 *
 * @returns {{changed: boolean, quantity: number, perPallet: number}|null}
 *          null — округлять нечего: позиция продаётся поштучно, нормы нет либо
 *          количество уже кратно поддону.
 */
export const roundUpsellQuantityToPallet = ({ material, quantity } = {}) => {
  if (!isPalletOnlyUpsell(material)) {
    return null;
  }

  const perPallet = unitsPerPalletOf(material);
  if (perPallet === null) {
    return null;
  }

  const requested = Number(String(quantity ?? '').toString().replace(',', '.'));
  if (!Number.isFinite(requested) || requested <= 0) {
    // Пустое поле не превращаем в поддон: менеджер, возможно, стирает значение,
    // чтобы набрать другое. Позицию без количества уберёт крестик.
    return null;
  }

  const pallets = Math.ceil(requested / perPallet);
  const rounded = pallets * perPallet;

  return { changed: rounded !== requested, quantity: rounded, perPallet };
};

/**
 * Сколько поддонов сейчас в позиции. Для показа в поле количества у U-блоков:
 * менеджер заказывает поддонами, штуки считает завод.
 */
export const upsellPalletsOf = (material) => {
  const perPallet = unitsPerPalletOf(material);
  if (perPallet === null) {
    return null;
  }
  const quantity = Number(material?.quantity);
  if (!Number.isFinite(quantity) || quantity <= 0) {
    return null;
  }
  return Math.ceil(quantity / perPallet);
};

/** Штук в заданном числе поддонов. Пустое поле остаётся пустым. */
export const upsellQuantityForPallets = (material, pallets) => {
  const perPallet = unitsPerPalletOf(material);
  if (perPallet === null) {
    return null;
  }
  const requested = Number(String(pallets ?? '').toString().replace(',', '.'));
  if (!Number.isFinite(requested) || requested <= 0) {
    return null;
  }
  return Math.max(Math.round(requested), 1) * perPallet;
};

export const unitsPerPalletOfUpsell = unitsPerPalletOf;

export const palletOnlyMessage = (perPallet, rounded) => (
  `U-блоки продаются целыми поддонами по ${perPallet} шт — количество увеличено до ${rounded} шт.`
);

/**
 * @param {object} params
 * @param {object} params.material        — редактируемая позиция заказа
 * @param {number} params.nextQuantity    — количество, которое ввёл менеджер
 * @param {number} params.freePallets     — свободные паллетоместа машины остатка
 * @returns {{ allowed: boolean, message?: string, extraPlaces?: number }}
 */
export const resolveUpsellQuantityChange = ({ material, nextQuantity, freePallets } = {}) => {
  const currentQuantity = Number(material?.quantity) || 0;
  const requestedQuantity = Number(nextQuantity);

  // Пустой ввод и уменьшение пропускаем без проверки вместимости.
  if (!Number.isFinite(requestedQuantity) || requestedQuantity <= currentQuantity) {
    return { allowed: true };
  }

  const currentPlaces = getUpsellPalletPlacesForQuantity(material, currentQuantity);
  const nextPlaces = getUpsellPalletPlacesForQuantity(material, requestedQuantity);

  // Норму взять неоткуда — увеличение блокируем, количество не выдумываем.
  if (currentPlaces === null || nextPlaces === null) {
    return { allowed: false, message: UPSELL_NO_NORM_MESSAGE };
  }

  // Сравниваем именно прирост: остальные паллетоместа позиции уже оплачены.
  const extraPlaces = nextPlaces - currentPlaces;
  if (extraPlaces <= 0) {
    return { allowed: true };
  }

  const available = Number.isFinite(Number(freePallets)) ? Math.max(Number(freePallets), 0) : 0;
  if (extraPlaces > available) {
    return {
      allowed: false,
      extraPlaces,
      message: `Не помещается: требуется ещё ${extraPlaces} ${palletsWord(extraPlaces)}, свободно ${available}.`,
    };
  }

  return { allowed: true, extraPlaces };
};

export default resolveUpsellQuantityChange;
